import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "@/api/api";
import { 
  Card, 
  CardBody, 
  Spinner, 
  Button, 
  Divider,
  Badge
} from "@heroui/react";
import { motion, AnimatePresence } from "framer-motion";

const CheckBadgeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-12 h-12">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-12 h-12">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  </svg>
);

const PublicVerifier: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<any>(null);
  
  const id = searchParams.get("id");
  const hash = searchParams.get("hash");

  useEffect(() => {
    const verify = async () => {
      try {
        const response = await apiClient.get(`/verify/qr?id=${id}&hash=${hash}`);
        setResult(response.data);
      } catch (err) {
        console.error("Verification failed", err);
        setResult({ valid: false, message: "Network error or invalid link" });
      } finally {
        setLoading(false);
      }
    };

    if (id && hash) {
      verify();
    } else {
      setLoading(false);
    }
  }, [id, hash]);

  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-center p-6 bg-gray-50 overflow-hidden relative">
      {/* Background Decor */}
      <div className="absolute inset-0 z-0 opacity-20">
         <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-teal-300 rounded-full filter blur-3xl"></div>
         <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-emerald-300 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-md w-full z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-teal-900 mb-2">Digital Twin Proof</h1>
          <p className="text-gray-500">Official Sila Green Agro Registry Lookup</p>
        </div>

        <Card className="w-full bg-white shadow-2xl border-none rounded-3xl overflow-hidden">
          <CardBody className="p-0">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div 
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-12 flex flex-col items-center gap-4"
                >
                  <Spinner size="lg" color="secondary" />
                  <p className="text-teal-700 font-bold animate-pulse">Consulting Ledger...</p>
                </motion.div>
              ) : result?.valid ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-8"
                >
                  <div className="flex flex-col items-center text-center space-y-6">
                    <Badge content="Verified" color="success" size="lg">
                      <div className="w-20 h-20 bg-green-100 text-green-700 rounded-2xl flex items-center justify-center">
                        <CheckBadgeIcon />
                      </div>
                    </Badge>

                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Authentic Record</h2>
                      <p className="text-green-600 text-sm font-medium">Digital signature matches the physical document.</p>
                    </div>

                    <Divider />

                    <div className="w-full space-y-4 text-left">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Animal</p>
                          <p className="font-bold text-gray-800">{result.animal.name}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Registration</p>
                          <p className="font-bold text-gray-800">{result.animal.gn}</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Breeder</p>
                        <p className="font-bold text-gray-800">
                          {result.animal.farmer?.firstname} {result.animal.farmer?.lastname}
                        </p>
                      </div>

                      <div className="bg-teal-50 p-3 rounded-xl border border-teal-100 flex gap-3 items-start">
                        <div className="w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-[10px] shrink-0">i</div>
                        <p className="text-[10px] text-teal-800 leading-relaxed">
                          This record represents the "Digital Twin" of the certificate. If the details on your screen match the paper document, the document is authentic.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-12 text-center space-y-6"
                >
                  <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto border border-red-100">
                    <AlertIcon />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-red-900">Invalid Link</h2>
                    <p className="text-gray-500 text-sm">The digital twin record could not be found or has been revoked.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardBody>
        </Card>

        <div className="mt-8">
          <Link to="/">
            <Button variant="light" color="secondary" fullWidth className="font-bold">
              Return to Sila Green Agro
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicVerifier;
